"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RequestCacheInterceptor = void 0;
const axios_1 = require("axios");
const cache_manager_1 = require("../../cache-manager/cache-manager");
const utils_1 = require("../../../utils");
class RequestCacheInterceptor {
    constructor() {
        this.debugLogger = new utils_1.DebugLogger('RequestCacheInterceptor');
        this.onRequest = async (config) => {
            const cachedValue = this.getFromCache(config);
            if (cachedValue) {
                if (cachedValue instanceof Promise) {
                    this.debugLogger.log(`Request for ${config.url} is already in progress, awaiting cached Promise`);
                    config.adapter = async () => cachedValue;
                }
                else {
                    this.debugLogger.log(`Serving request for ${config.url} from cache`);
                    config._requestCached = true;
                    config.adapter = async () => this.cloneResponse(cachedValue);
                }
            }
            else {
                this.debugLogger.log(`No cached response for ${config.url}, proceeding with network request`);
                config.adapter = async (newConfig) => {
                    const requestPromise = (0, axios_1.getAdapter)('xhr')(newConfig);
                    this.storeToCache(requestPromise, newConfig);
                    this.debugLogger.log(`Request promise for ${newConfig.url} stored in cache`);
                    return requestPromise;
                };
            }
            return config;
        };
        this.onResponse = (response) => {
            if (response.config._requestCached) {
                this.debugLogger.log(`Response for ${response.config.url} was served from cache, not storing again`);
                return response;
            }
            this.storeToCache(response, response.config);
            this.debugLogger.log(`Response for ${response.config.url} stored in cache`);
            return response;
        };
        this.onError = (error) => {
            if (!(error === null || error === void 0 ? void 0 : error.config)) {
                this.debugLogger.log(`An error occurred without request config, cannot perform cache operations`);
                return Promise.reject(error);
            }
            if (error.config._requestCached) {
                this.debugLogger.log(`An error occurred for ${error.config.url}, but request was served from cache`);
                return Promise.reject(error);
            }
            this.deleteFromCache(error.config);
            this.debugLogger.log(`Request for ${error.config.url} failed, removed from cache`);
            return Promise.reject(error);
        };
        this.cacheManager = cache_manager_1.CacheManagerProvider.getInstance();
    }
    runWhen(config) {
        return !!(config === null || config === void 0 ? void 0 : config.cache);
    }
    getCacheKey(config) {
        if (typeof config.cache === 'object' && config.cache.key) {
            return config.cache.key;
        }
        if (typeof config.cache === 'string') {
            return config.cache;
        }
        return null;
    }
    getFromCache(config) {
        const key = this.getCacheKey(config);
        if (!key) {
            return undefined;
        }
        return this.cacheManager.get(key);
    }
    storeToCache(data, config) {
        const key = this.getCacheKey(config);
        if (!key) {
            return;
        }
        const cacheTtl = typeof config.cache === 'object' ? config.cache.ttlMs : undefined;
        this.cacheManager.set(key, data, cacheTtl);
    }
    deleteFromCache(config) {
        const key = this.getCacheKey(config);
        if (!key) {
            return;
        }
        this.cacheManager.delete(key);
    }
    cloneResponse(response) {
        return {
            ...response,
            data: this.deepClone(response.data),
            headers: { ...response.headers },
            config: { ...response.config }
        };
    }
    deepClone(obj) {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }
        if (obj instanceof Date) {
            return new Date(obj.getTime());
        }
        if (obj instanceof Array) {
            return obj.map(item => this.deepClone(item));
        }
        if (obj instanceof Object) {
            const clonedObj = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    clonedObj[key] = this.deepClone(obj[key]);
                }
            }
            return clonedObj;
        }
        return obj;
    }
}
exports.RequestCacheInterceptor = RequestCacheInterceptor;
